import {format} from 'date-fns';
import {cloneDeep} from 'lodash';
import React, {useEffect, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {createFragmentContainer, graphql} from 'react-relay';
import {Redirect, Route, Switch, useHistory, useLocation, useRouteMatch, withRouter} from 'react-router-dom';
import {ELEMENT_TYPE, MODULE_TYPES} from '../../constants';
import {projectUrl, quotePdfSrc} from '../../directApi';
import ProjectHeader from '../../forecast-app/project-tab/projects/shared/ProjectHeader';
import EmptyState from '../../forecast-app/shared/components/empty-states/empty_state';
import HeaderBar from '../../forecast-app/shared/components/headers/header-bar/header_bar';
import {MODAL_TYPE, showModal} from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import {showSendQuoteModal} from '../../forecast-app/shared/components/modals/send-quote/SendQuoteModal';
import {getProjectIndicatorString} from '../../forecast-app/shared/components/project-indicator/support/ProjectIndicatorLogic';
import {isQuotesEnabled} from '../../forecast-app/shared/util/FeatureUtil';
import {idFromGlobalId} from '../../forecast-app/shared/util/GlobalIdUtil';
import {hasModule} from '../../forecast-app/shared/util/ModuleUtil';
import {hasPermission} from '../../forecast-app/shared/util/PermissionsUtil';
import ProjectUtil from '../../forecast-app/shared/util/ProjectUtil';
import Util from '../../forecast-app/shared/util/util';
import {PERMISSION_TYPE} from '../../Permissions';
import {withSocketHandling} from '../../socket/withSocketHandling';
import * as tracking from '../../tracking';
import {useTrackPage} from '../../tracking/amplitude/hooks/useTrackPage';
import DeliverablesGuideSection from '../baseline/DeliverablesGuideSection';
import {DeliverablesPhasesSection} from '../baseline/DeliverablesPhasesSection';
import {
	ContentContainer,
	InitialPlanPageWrapper,
	AlertContainer,
	LockedBannerContainer,
	QuoteActivityContainer,
} from './InitialPlan.styled';
import {bannerMessageId, isLiveQuote, LOCKED_QUOTE_STATUSES} from '../baseline/quote-activity/quoteActivityCopy';
import {QuoteActivitySection} from '../baseline/quote-activity/QuoteActivitySection';
import {getSocketConfig} from './InitialPlanSocket';
import {getGroupedFinancialNumbersByPhaseAndBaselineId} from './InitialPlanUtil';
import BaselinePDF from './pdf/BaselinePDF';
import {formatPhases, formatProject} from './pdf/BaselinePDFFormatter';
import {Settings} from './Settings';
import {Alert, Button, EMPHASIS} from '@forecast-it/design-system';
import {InitialPlanPage_viewer$data} from './__generated__/InitialPlanPage_viewer.graphql';

// Gated on the STATUS, not on a quote merely existing: an approved, declined or revised quote
// must not offer Send. SENT is included on purpose — resending is a first-class action.
const SENDABLE_QUOTE_STATUSES = ['PUBLISHED', 'SENT'];

type Project = NonNullable<InitialPlanPage_viewer$data['project']>;
type Company = NonNullable<InitialPlanPage_viewer$data['company']>;
type PhaseEdge = NonNullable<Project['phases']>['edges'] extends ReadonlyArray<infer Edge> | null ? Edge : never;

type HeaderBarButton = {
	type: string;
	emphasis: EMPHASIS;
	text: string;
	callback: () => void;
	dataCy: string;
	icon?: string;
	rightIcon?: string;
	disabled?: boolean;
};

type InitialPlanPageProps = {
	viewer: InitialPlanPage_viewer$data;
	retry: () => void;
	setSocketConfig: (config: ReturnType<typeof getSocketConfig>) => void;
};

type InitialPlanPageContentProps = InitialPlanPageProps & {
	project: Project;
	company: Company;
};

const InitialPlanPageContent = ({
	viewer,
	project,
	company,
	retry,
	setSocketConfig,
}: InitialPlanPageContentProps): JSX.Element => {
	const intl = useIntl();
	const {totalFinancialNumbers, financialNumbersMap: groupedFinancialNumbers} =
		getGroupedFinancialNumbersByPhaseAndBaselineId(project.groupedFinancialNumbers ?? []);
	const SETTINGS_PATH = 'settings';
	const {path} = useRouteMatch();
	const location = useLocation();
	const history = useHistory();
	const currency = project.rateCard ? project.rateCard.currency : company.currency;
	const currencySymbol = Util.GetCurrencySymbol(currency);
	const hasDemandAccess =
		(Util.AuthorizeViewerAccess('scheduling') &&
			hasPermission(PERMISSION_TYPE.SCHEDULING_ACCESS) &&
			hasModule(MODULE_TYPES.PLACEHOLDERS)) ||
		hasPermission(PERMISSION_TYPE.DEMAND_VIEW_ONLY);

	const [showPlaceholderAlert, setShowPlaceholderAlert] = useState(false);
	const [placeholderCount, setPlaceholderCount] = useState(0);
	const [placeholderAllocationCount, setPlaceholderAllocationCount] = useState(0);

	// A snapshot of the phase order taken on mount; the PDF export lists phases in this order.
	const [phaseSortOrder] = useState(
		Util.sortBaselinePhase(cloneDeep(project.phases?.edges ?? [])).flatMap((phase: PhaseEdge) =>
			phase?.node ? [phase.node.id] : []
		)
	);

	useTrackPage('Baseline');

	useEffect(() => {
		const name = project
			? project.name !== null && project.name !== ''
				? project.name
				: getProjectIndicatorString(project.companyProjectId, project.customProjectId)
			: null;
		document.title = `${intl.formatMessage({id: 'project_section.baseline'})} - ${name} - Forecast`;
		tracking.trackPage('Baseline');

		const projectId = parseInt(atob(project.id).replace('ProjectType:', ''));
		setSocketConfig(getSocketConfig(projectId));
	}, []);

	// Redirect if not a baseline project
	if (!Util.isBaselineProject(company.modules, project)) {
		return <Redirect to={projectUrl(project.companyProjectId, project.customProjectId) + '/workflow/'} />;
	}

	const shouldBlockBaseline = useMemo(() => {
		return project.projectStartYear === null || project.projectEndYear === null;
	}, [project.projectStartYear, project.projectEndYear]);

	const quote = project.quote;
	const quotesEnabled = isQuotesEnabled();
	// Lock state derives entirely from the quote's status — nothing is stored, so nothing can desync.
	const baselineLocked = quotesEnabled && !!quote?.status && LOCKED_QUOTE_STATUSES.includes(quote.status);
	// Availability is a gate on the banner in its own right. It used to ride along on
	// baselineLocked, which the banner no longer reads — without it here, a company without the
	// quotes module would see a lock banner for a quote it cannot even open.
	const lockedBannerMessageId = quotesEnabled && quote ? bannerMessageId(quote.status) : null;
	const baselineUrl = `${projectUrl(project.companyProjectId, project.customProjectId)}/baseline`;
	const goToQuoteDesigner = () => history.push(`${baselineUrl}/quote`);
	// The published PDF opens in the browser's own viewer — no in-app quote view.
	const openQuotePdf = () => {
		if (quote) window.open(quotePdfSrc(quote.id), '_blank', 'noopener');
	};

	const addProjectDates = () => {
		showModal({
			type: MODAL_TYPE.ADD_PROJECT_DATES,
			project: project,
			datesSetCallback: () => retry(),
		});
	};

	const pdfExport = useMemo(() => {
		const minutesPerDay = project.baselineEstimationMinutesPerDay ?? null;

		const exportProject = formatProject({
			project,
			totalFinancialNumbers,
			currencySymbol,
			intl,
			minutesPerDay,
		});

		const exportPhases = formatPhases({
			phases: project.phases?.edges ?? [],
			groupedFinancialNumbers,
			phaseSortOrder,
			currencySymbol,
			intl,
			minutesPerDay,
		});

		const exportDate = format(new Date(), 'PP');

		return {
			fileName: `${project.name}_baseline`,
			document: (
				<BaselinePDF
					client={project.client}
					project={exportProject}
					phases={exportPhases}
					creatorName={viewer.fullName ?? ''}
					companyName={company.name ?? ''}
					date={exportDate}
					intl={intl}
				/>
			),
		};
	}, [viewer, totalFinancialNumbers, currencySymbol, intl, groupedFinancialNumbers, phaseSortOrder]);

	const useDeliverables = ProjectUtil.deliverablesUiEnabled(project);

	const projectDeliverablesSet = useMemo(
		() => new Set((project.deliverables?.edges ?? []).flatMap(edge => (edge?.node ? [idFromGlobalId(edge.node.id)] : []))),
		[project.deliverables]
	);

	const hasBaselineItems = useMemo(() => {
		const validBaselineItems = [
			...(project.phaseBaselineRoles?.edges ?? []),
			...(project.phaseBaselineExpenses?.edges ?? []),
		].filter(edge => {
			const node = edge?.node;
			if (!node) return false;
			return useDeliverables ? node.deliverableId && projectDeliverablesSet.has(node.deliverableId) : true;
		});

		return validBaselineItems.length > 0;
	}, [useDeliverables, projectDeliverablesSet, project.phaseBaselineRoles, project.phaseBaselineExpenses]);

	const showSendQuote =
		quotesEnabled &&
		!!quote?.status &&
		SENDABLE_QUOTE_STATUSES.includes(quote.status) &&
		hasPermission(PERMISSION_TYPE.QUOTE_SEND);

	const openSendQuoteModal = () => {
		if (!quote) return;
		showSendQuoteModal({
			quoteId: quote.id,
			projectName: project.name ?? '',
			projectIndicator: getProjectIndicatorString(project.companyProjectId, project.customProjectId),
			clientContacts: (project.client?.clientContacts?.edges || []).flatMap(edge => (edge?.node ? [edge.node] : [])),
			// Only the contact linked to the opportunity is pre-selected; the rest stay in the picker.
			linkedContactId: project.clientContact?.id ?? null,
			// The name of the user-visible filed copy; the bytes themselves come from the quote's
			// archive server-side, so a deleted file row means a missing NAME, not a missing attachment.
			pdfFileName: quote.pdfFile?.name ?? null,
			viewerName: viewer.fullName ?? '',
			companyName: company.name ?? '',
			// Without it every send analytics event is unattributable to a company.
			companyId: company.id,
			companyProjectId: project.companyProjectId,
			revisionNumber: quote.revisionNumber,
		});
	};
	// The create CTA needs all five gates; viewing needs a LIVE quote, not merely a row: a revising
	// quote reopens Create and withdraws View/Send. Only the quote-existence term of each moves —
	// the permission and baseline-items gates are exactly as they were.
	const showCreateQuote =
		quotesEnabled && !isLiveQuote(quote?.status) && hasPermission(PERMISSION_TYPE.QUOTE_CREATE) && hasBaselineItems;
	const showViewQuote = quotesEnabled && isLiveQuote(quote?.status);

	const getHeaderBar = () => {
		const rightContent: HeaderBarButton[] = [];
		if (showCreateQuote) {
			rightContent.push({
				type: ELEMENT_TYPE.SKYLINE_BUTTON,
				emphasis: EMPHASIS.HIGH,
				text: intl.formatMessage({id: 'baseline.create_quote'}),
				callback: goToQuoteDesigner,
				dataCy: 'create-quote-button',
			});
		}
		if (showViewQuote) {
			rightContent.push({
				type: ELEMENT_TYPE.SKYLINE_BUTTON,
				emphasis: EMPHASIS.MEDIUM,
				icon: 'view',
				text: intl.formatMessage({id: 'baseline.view_quote'}),
				callback: openQuotePdf,
				dataCy: 'view-quote-button',
			});
		}
		// Outside the View block on purpose: View is availability-gated only, while Send also
		// needs a sendable status and the permission, so the two conditions are no longer the same.
		if (showSendQuote) {
			rightContent.push({
				type: ELEMENT_TYPE.SKYLINE_BUTTON,
				emphasis: EMPHASIS.HIGH,
				icon: 'mail',
				text: intl.formatMessage({id: 'baseline.send_quote'}),
				callback: openSendQuoteModal,
				dataCy: 'send-quote-button',
			});
		}
		rightContent.push({
			type: ELEMENT_TYPE.SKYLINE_BUTTON,
			emphasis: EMPHASIS.MEDIUM,
			rightIcon: 'settings',
			...(baselineLocked ? {icon: 'lockClosed', disabled: true} : {}),
			text: intl.formatMessage({id: 'baseline.settings'}),
			callback: () => history.push(`${location.pathname}/${SETTINGS_PATH}`),
			dataCy: 'baseline-settings-button',
		});
		return <HeaderBar leftContent={[]} rightContent={rightContent} />;
	};

	// Copy is status-driven and the message map is the ONLY gate: lockedBannerMessageId is null for
	// any status with no key, so an unmapped status renders no banner rather than an empty Alert.
	// baselineLocked is deliberately NOT part of this condition — it is true only when locked, so
	// gating on it would make the unlocked REVISING banner unreachable no matter what is mapped.
	// It still drives every other locked affordance on this page; it just stops gating this one.
	// Every quote status uses the same INFORMATION alert, unlocked included: this banner reports
	// where the quote is, and one appearance across the whole lifecycle keeps it reading as a
	// single status line rather than escalating on one state. The data-cy still tells the two
	// apart for tests.
	const quoteHeader = (
		<>
			{lockedBannerMessageId && (
				<LockedBannerContainer data-cy={baselineLocked ? 'baseline-locked-banner' : 'baseline-unlocked-banner'}>
					<Alert type={Alert.TYPE.INFORMATION} text={intl.formatMessage({id: lockedBannerMessageId})} />
				</LockedBannerContainer>
			)}
			{quotesEnabled && !!quote && (
				<QuoteActivityContainer>
					<QuoteActivitySection project={project} companyId={company.id} onDecided={retry} />
				</QuoteActivityContainer>
			)}
		</>
	);

	return (
		<Switch>
			<Route exact path={[path, `${path}/T:taskId(\\d+)`]}>
				<InitialPlanPageWrapper data-cy={'baseline-page'}>
					{showPlaceholderAlert && (
						<AlertContainer>
							<Alert
								type={Alert.TYPE.SUCCESS}
								text={intl.formatMessage(
									{id: 'convert_baseline_to_placeholders.modal.success'},
									{placeholderCount, placeholderAllocationCount}
								)}
								onDismiss={() => setShowPlaceholderAlert(false)}
							>
								{hasDemandAccess && (
									<Button
										emphasis="medium"
										onPress={() => {
											// Navigate to scheduling page to see the created placeholders
											history.push('/scheduling#demand');
										}}
									>
										<FormattedMessage id="convert_baseline_to_placeholders.modal.view_placeholders" />
									</Button>
								)}
							</Alert>
						</AlertContainer>
					)}
					<ProjectHeader
						title={intl.formatMessage({id: 'project_section.baseline'})}
						buttons={getHeaderBar()}
						project={project}
						psProject={viewer.psProject}
					/>

					{/* The Baseline sections render the quote header inside their own scroll container, so the
					    Baseline scrolls as one region; the empty state owns no such container and keeps it here. */}
					{shouldBlockBaseline ? (
						<>
							{quoteHeader}
							<div style={{width: '100%'}} data-cy="baseline-empty-state">
								<EmptyState
									pageName={EmptyState.EMPTY_STATE.BASELINE_NO_DATES}
									callback={hasPermission(PERMISSION_TYPE.PROJECTS_UPDATE) && addProjectDates}
								/>
							</div>
						</>
					) : (
						<ContentContainer>
							{hasBaselineItems ? (
								<DeliverablesPhasesSection
									topContent={quoteHeader}
									project={project}
									company={company}
									pdfProps={pdfExport}
									pageRetry={retry}
									currencySymbol={currencySymbol}
									useDeliverables={useDeliverables}
									baselineLocked={baselineLocked}
									setShowPlaceholderAlert={setShowPlaceholderAlert}
									setPlaceholderCount={setPlaceholderCount}
									setPlaceholderAllocationCount={setPlaceholderAllocationCount}
								/>
							) : (
								<DeliverablesGuideSection
									topContent={quoteHeader}
									project={project}
									useDeliverables={useDeliverables}
									baselineLocked={baselineLocked}
									currency={currency ?? undefined}
								/>
							)}
						</ContentContainer>
					)}
				</InitialPlanPageWrapper>
			</Route>
			<Route exact path={[`${path}/${SETTINGS_PATH}`, `${path}/${SETTINGS_PATH}/T:taskId(\\d+)`]}>
				{baselineLocked ? (
					<Redirect to={baselineUrl} />
				) : (
					<Settings
						company={company}
						rateCards={company.rateCards?.edges}
						project={project}
						baselineMinutesPerDay={project.baselineEstimationMinutesPerDay}
						baselineTarget={project.baselineTarget}
						budgetType={project.budgetType}
						budget={project.budget}
						unassignedTaskHourlyCost={project.unassignedTaskHourlyCost}
						selectedRateCardId={project.rateCard?.id}
						taskCostCalculationType={project.taskCostCalculationType}
						baselineWinChance={project.baselineWinChance}
						salesforceOpportunity={project.salesforceOpportunity}
						currency={currency}
					/>
				)}
			</Route>
			<Route path="*">
				<Redirect to="/not-found" />
			</Route>
		</Switch>
	);
};

// The query renderer renders this page once the viewer has loaded; the project and company arrive with it.
export const InitialPlanPage = (props: InitialPlanPageProps): JSX.Element | null => {
	const {project, company} = props.viewer;
	return project && company ? <InitialPlanPageContent {...props} project={project} company={company} /> : null;
};

export const InitialPlanPageQuery = graphql`
	query InitialPlanPage_Query($projectId: String) {
		viewer {
			actualPersonId
			component(name: "project_initial_plan")
			project(id: $projectId) {
				id
			}
			...InitialPlanPage_viewer @arguments(projectId: $projectId)
		}
	}
`;

export default withRouter(
	withSocketHandling(
		createFragmentContainer(InitialPlanPage, {
			viewer: graphql`
				fragment InitialPlanPage_viewer on Viewer @argumentDefinitions(projectId: {type: "String"}) {
					id
					fullName
					company {
						id
						name
						currency
						monday
						tuesday
						wednesday
						thursday
						friday
						saturday
						sunday
						roles(first: 1000000) @connection(key: "Company_roles", filters: []) {
							edges {
								node {
									id
									name
								}
							}
						}
						rateCards {
							edges {
								...RateCardDropdown_rateCards
								node {
									id
									disabledRoles {
										id
									}
								}
							}
						}
						modules {
							moduleType
						}
						expenseCategories(first: 1000000) @connection(key: "Company_expenseCategories", filters: []) {
							edges {
								node {
									id
									name
								}
							}
						}
						dateEffectiveExchangeRates(first: 10000) {
							edges {
								node {
									id
									currency
									rate
									startDate
								}
							}
						}
						isUsingProjectAllocation
						isUsingMixedAllocation
					}
					project(id: $projectId) {
						...ProjectHeader_project
						...SecondaryNavigation_project
						...HeaderSection_project
						useBaseline
						useDeliverables
						budgetType
						budget
						id
						stage {
							id
							name
							profile
						}
						companyProjectId
						customProjectId
						name
						unassignedTaskHourlyCost
						taskCostCalculationType
						synchBaselineAndScopingDates
						useFixedPriceForBaselineRevenue
						baselineWinChance
						baselineTarget
						baselineEstimationMinutesPerDay
						projectStartYear
						projectStartMonth
						projectStartDay
						projectEndYear
						projectEndMonth
						projectEndDay
						hubspotDeal
						salesforceOpportunity
						quote {
							id
							status
							revisionNumber
							pdfFile {
								name
							}
						}
						...QuoteActivitySection_project
						rateCard {
							id
							currency
							disabledRoles {
								id
							}
						}
						clientContact {
							id
						}
						client {
							name
							street
							city
							zip
							vat
							clientContacts(first: 1000000) {
								edges {
									node {
										id
										firstName
										lastName
										email
									}
								}
							}
						}
						financialNumbers(convertToProjectCurrency: true) {
							baselineMinutes
						}
						phases(first: 1000000) @connection(key: "Project_phases", filters: []) {
							edges {
								node {
									id
									name
									startYear
									startMonth
									startDay
									deadlineYear
									deadlineMonth
									deadlineDay
									sortOrder
									baselineStartYear
									baselineStartMonth
									baselineStartDay
									baselineDeadlineDay
									baselineDeadlineMonth
									baselineDeadlineYear
									projectId
									portalConfig {
										requiresClientSignoff
										signoffApproverPortalUserId
										signoffLocked
									}
									phaseBaselineRoles(first: 100000)
										@connection(key: "Phase_phaseBaselineRoles", filters: []) {
										edges {
											node {
												id
												baselineMinutes
												role {
													name
												}
											}
										}
									}
									phaseBaselineExpenses(first: 100000)
										@connection(key: "Phase_phaseBaselineExpenses", filters: []) {
										edges {
											node {
												id
												expenseCategory {
													name
												}
											}
										}
									}
									phaseDeliverables(first: 10000) @connection(key: "Phase_phaseDeliverables", filters: []) {
										edges {
											node {
												id
												phaseId
												deliverable {
													id
													name
												}
												financialNumbers(convertToProjectCurrency: true) {
													baselineRevenue
												}
											}
										}
									}
								}
							}
						}
						phaseBaselineExpenses(first: 1000000) @connection(key: "Project_phaseBaselineExpenses", filters: []) {
							edges {
								node {
									id
									expenseName
									expenseCategory {
										name
										id
									}
									expenseRevenue
									expenseCost
									expenseMarkup
									expenseProfit
									globalPhaseId
									deliverableId
								}
							}
						}
						phaseBaselineRoles(first: 1000000) @connection(key: "Project_phaseBaselineRoles", filters: []) {
							edges {
								node {
									id
									globalPhaseId
									deliverableId
									baselineMinutes
									role {
										name
										id
									}
									financialNumbers(convertToProjectCurrency: true) {
										baselineTimeAndExpenses
										baselineRevenue
										baselineCost
										baselineProfit
										baselineRatePerHour
										baselineCostPerHour
									}
								}
							}
						}
						deliverables(first: 1000000) @connection(key: "Project_deliverables", filters: []) {
							edges {
								node {
									id
									name
									description
									price
									invoicedAmount
									lockedRevenue
									hasBeenInvoiced
									hasLockedRevenue
								}
							}
						}
						groupedFinancialNumbers(
							convertToProjectCurrency: true
							groupBy: ["PHASE_BASELINE_ROLE", "PHASE_BASELINE_EXPENSE", "PHASE"]
						) {
							baselineRevenue
							baselineTimeAndExpenses
							baselineCost
							baselineProfit
							baselineRatePerHour
							phaseBaselineRoleId
							phaseBaselineExpenseId
							phaseId
						}
					}
					psProject(companyProjectId: $projectId) {
						...ProjectHeader_psProject
					}
				}
			`,
		})
	)
);
